import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { fetchData } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import {
  Button,
  Card,
  Currency,
  Empty,
  ErrorBanner,
  Field,
  FormattedDate,
  Input,
  Loading,
  PageHeader,
  Select,
  Tag,
} from '../../components/ui'
import { flagIfAuthError } from '../../components/ResetBanner'

// GST side bifurcation (#450 / A22):
//  - Sales GST  = output GST charged on income (income-wise report).
//  - Purchase GST  = input-tax-credit GST on vendor bills (vendor-wise report).
type PeriodType = 'month' | 'quarter' | 'fy' | 'custom'
type Side = 'sales' | 'input'

type PeriodState = {
  period_type: PeriodType
  period_anchor: string
  from: string
  to: string
}

type ResolvedPeriod = { type: string; from: number; to: number; label: string }

type ExportResponse = { format: string; content: string; pdf_url?: string | null }

type SalesEntry = {
  id: string
  date: number
  type: string
  description: string | null
  source: string | null
  amount: number
  gst_percentage: number
  gst_amount: number
}

type SalesReport = {
  gst_bucket: 'sales_gst'
  gst_label: string
  period: ResolvedPeriod
  totals: { total_amount: number; total_gst: number }
  entries: SalesEntry[]
  pagination: { total: number; limit: number; offset: number }
}

type VendorRow = {
  vendor_id: string
  vendor_name: string
  gst_number: string | null
  bills_count: number
  cgst: number
  sgst: number
  igst: number
  total_gst: number
}

type VendorReport = {
  gst_bucket: 'input_gst'
  gst_label: string
  period: ResolvedPeriod
  totals: {
    cgst: number
    sgst: number
    igst: number
    total_gst: number
    vendors_count: number
    bills_count: number
  }
  vendors: VendorRow[]
  pagination: { total: number; limit: number; offset: number }
}

function buildPeriodQuery(p: PeriodState): string {
  const params = new URLSearchParams()
  params.set('period_type', p.period_type)
  if (p.period_type === 'custom') {
    if (p.from) params.set('from_date', String(Math.floor(new Date(`${p.from}T00:00:00Z`).getTime() / 1000)))
    if (p.to) params.set('to_date', String(Math.floor(new Date(`${p.to}T23:59:59Z`).getTime() / 1000)))
  } else if (p.period_anchor.trim()) {
    params.set('period_anchor', p.period_anchor.trim())
  }
  params.set('limit', '200')
  return params.toString()
}

function openExportedPdf(res: ExportResponse) {
  if (res.pdf_url) window.open(res.pdf_url, '_blank', 'noopener,noreferrer')
}

export function GstReportPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const [side, setSide] = useState<Side>('sales')
  const [period, setPeriod] = useState<PeriodState>({
    period_type: 'fy',
    period_anchor: '',
    from: '',
    to: '',
  })

  const qs = buildPeriodQuery(period)
  const base = `/projects/${projectId}/accounts/reports/gst`

  const sales = useQuery<SalesReport>({
    queryKey: ['gst-income', projectId, qs],
    enabled: Boolean(projectId) && side === 'sales',
    queryFn: () => fetchData<SalesReport>(orgPath(`${base}/income?${qs}`)),
  })

  const vendors = useQuery<VendorReport>({
    queryKey: ['gst-vendors', projectId, qs],
    enabled: Boolean(projectId) && side === 'input',
    queryFn: () => fetchData<VendorReport>(orgPath(`${base}/vendors?${qs}`)),
  })

  const exportPdf = useMutation({
    mutationFn: () => {
      const path = side === 'sales' ? `${base}/income/export` : `${base}/vendors/export`
      return fetchData<ExportResponse>(orgPath(`${path}?${qs}&format=pdf`))
    },
    onError: flagIfAuthError,
    onSuccess: openExportedPdf,
  })

  const active = side === 'sales' ? sales : vendors
  const resolved = side === 'sales' ? sales.data?.period : vendors.data?.period
  const label = side === 'sales' ? 'Sales GST' : 'Purchase GST'

  return (
    <div className="space-y-5">
      <PageHeader
        title="GST report"
        subtitle="Bifurcated by side: Sales GST (output, charged on income) vs Purchase GST (input-tax-credit on vendor bills)."
      />

      {/* Side toggle */}
      <div className="flex gap-2">
        {(['sales', 'input'] as const).map((s) => (
          <Button
            key={s}
            variant={side === s ? 'primary' : 'ghost'}
            onClick={() => setSide(s)}
          >
            {s === 'sales' ? 'Sales GST' : 'Purchase GST'}
          </Button>
        ))}
      </div>

      {/* Period selector */}
      <Card>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Period type">
            <Select
              value={period.period_type}
              onChange={(e) =>
                setPeriod((p) => ({ ...p, period_type: e.target.value as PeriodType }))
              }
            >
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="fy">Financial year</option>
              <option value="custom">Custom range</option>
            </Select>
          </Field>
          {period.period_type === 'custom' ? (
            <>
              <Field label="From">
                <Input
                  type="date"
                  value={period.from}
                  onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
                />
              </Field>
              <Field label="To">
                <Input
                  type="date"
                  value={period.to}
                  onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
                />
              </Field>
            </>
          ) : (
            <Field
              label="Anchor (optional)"
              hint="e.g. 2026-04 (month), 2026-Q1 (quarter), 2026-27 (FY). Blank = current."
            >
              <Input
                value={period.period_anchor}
                onChange={(e) => setPeriod((p) => ({ ...p, period_anchor: e.target.value }))}
                placeholder="current"
              />
            </Field>
          )}
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Tag tone={side === 'sales' ? 'success' : 'warn'}>{label}</Tag>
          {resolved && <span className="text-sm text-surface-400">{resolved.label}</span>}
        </div>
        <Button
          variant="ghost"
          onClick={() => exportPdf.mutate()}
          disabled={exportPdf.isPending}
        >
          {exportPdf.isPending ? 'Exporting…' : 'Export PDF'}
        </Button>
      </div>
      {exportPdf.isError && <ErrorBanner error={exportPdf.error} />}

      {active.isLoading && <Loading />}
      {active.isError && <ErrorBanner error={active.error} />}

      {side === 'sales' && sales.data && <SalesGstView report={sales.data} />}
      {side === 'input' && vendors.data && <InputGstView report={vendors.data} />}
    </div>
  )
}

function SalesGstView({ report }: { report: SalesReport }) {
  return (
    <div className="space-y-4">
      <Card>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          <Totals label="Taxable amount" value={report.totals.total_amount} />
          <Totals label="Total GST" value={report.totals.total_gst} />
          <Totals label="Entries" count={report.pagination.total} />
        </div>
      </Card>
      {report.entries.length === 0 ? (
        <Empty message="No GST-bearing income in this period." />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-surface-400 border-b border-surface-700">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Description</th>
                  <th className="py-2 pr-4 text-right">Amount</th>
                  <th className="py-2 pr-4 text-right">GST %</th>
                  <th className="py-2 text-right">GST</th>
                </tr>
              </thead>
              <tbody>
                {report.entries.map((e) => (
                  <tr key={e.id} className="border-b border-surface-700/50">
                    <td className="py-2 pr-4 whitespace-nowrap"><FormattedDate ts={e.date} /></td>
                    <td className="py-2 pr-4">{e.type.replace(/_/g, ' ')}</td>
                    <td className="py-2 pr-4 text-surface-300">{e.description ?? '—'}</td>
                    <td className="py-2 pr-4 text-right"><Currency amount={e.amount} /></td>
                    <td className="py-2 pr-4 text-right text-surface-400">{e.gst_percentage}%</td>
                    <td className="py-2 text-right font-medium"><Currency amount={e.gst_amount} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}

function InputGstView({ report }: { report: VendorReport }) {
  return (
    <div className="space-y-4">
      <Card>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
          <Totals label="Total GST" value={report.totals.total_gst} />
          <Totals label="CGST" value={report.totals.cgst} />
          <Totals label="SGST" value={report.totals.sgst} />
          <Totals label="IGST" value={report.totals.igst} />
          <Totals label="Vendors" count={report.totals.vendors_count} />
          <Totals label="Bills" count={report.totals.bills_count} />
        </div>
      </Card>
      {report.vendors.length === 0 ? (
        <Empty message="No GST-bearing vendor bills in this period." />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-surface-400 border-b border-surface-700">
                  <th className="py-2 pr-4">Vendor</th>
                  <th className="py-2 pr-4">GSTIN</th>
                  <th className="py-2 pr-4 text-right">Bills</th>
                  <th className="py-2 pr-4 text-right">CGST</th>
                  <th className="py-2 pr-4 text-right">SGST</th>
                  <th className="py-2 pr-4 text-right">IGST</th>
                  <th className="py-2 text-right">Total GST</th>
                </tr>
              </thead>
              <tbody>
                {report.vendors.map((v) => (
                  <tr key={v.vendor_id} className="border-b border-surface-700/50">
                    <td className="py-2 pr-4">{v.vendor_name}</td>
                    <td className="py-2 pr-4 font-mono text-[12px] text-surface-400">
                      {v.gst_number ?? '—'}
                    </td>
                    <td className="py-2 pr-4 text-right">{v.bills_count}</td>
                    <td className="py-2 pr-4 text-right"><Currency amount={v.cgst} /></td>
                    <td className="py-2 pr-4 text-right"><Currency amount={v.sgst} /></td>
                    <td className="py-2 pr-4 text-right"><Currency amount={v.igst} /></td>
                    <td className="py-2 text-right font-medium"><Currency amount={v.total_gst} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}

function Totals({ label, value, count }: { label: string; value?: number; count?: number }) {
  return (
    <div>
      <div className="text-xs text-surface-400">{label}</div>
      <div className="text-lg font-semibold mt-0.5">
        {count !== undefined ? count : <Currency amount={value ?? 0} />}
      </div>
    </div>
  )
}
